/* eslint-disable @typescript-eslint/naming-convention */
// types
import { MRONativeLocalCart } from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { CartActionTypes as MROCartActionTypes } from "@cart-client/reducers/CartReducer";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/cart-types";
// utils
import { getBridgeSdk, isQuickCheckoutFlow } from "@swiggy-private/checkout-core";
import { getMROCartType, validateAllSubcartsSelectedInMROCart } from "@cart-client/helpers/MROFoodCart";

/**
 * State shape for the MRO cart reducer.
 * - uniqueIdForCart: Unique identifier for the MRO cart session.
 * - localCart: Local (client-side) representation of the MRO cart.
 * - reviewedCart: Server-reviewed MRO cart state.
 */
export interface MROCartState {
    uniqueIdForCart: string;
    localCart: MRONativeLocalCart | null;
    reviewedCart: MROReviewedCart | null;
}

/**
 * Action definitions for MRO cart reducer.
 */
export type MROCartAction =
    | { type: MROCartActionTypes.UPDATE_LOCAL_CART; payload: MRONativeLocalCart }
    | { type: MROCartActionTypes.UPDATE_REVIEWED_CART; payload: MROReviewedCart }
    | {
          type: MROCartActionTypes.UPDATE_CART;
          payload: {
              uniqueIdForCart: string;
              localCart: MRONativeLocalCart | null;
              reviewedCart: MROReviewedCart;
          };
      }
    | { type: MROCartActionTypes.CLEAR_LOCAL_CART; payload: { reviewedCart: MROReviewedCart } };

/**
 * Reducer for managing MRO cart state.
 * Handles local and reviewed cart updates, and syncs with native bridge when needed.
 */
export function MROCartReducer(state: MROCartState, action: MROCartAction): MROCartState {
    const { localCart, reviewedCart } = state || {};
    const { type, payload } = action || {};

    switch (type) {
        case MROCartActionTypes.UPDATE_LOCAL_CART: {
            // Update only the local MRO cart and sync with bridge
            const updatedCart = {
                ...state,
                localCart: { ...localCart, ...payload },
            };
            getBridgeSdk().updateLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
                    marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                    cartType: getMROCartType(),
                },
                data: updatedCart.localCart,
            });
            return updatedCart;
        }
        case MROCartActionTypes.UPDATE_REVIEWED_CART:
            // Update only the reviewed MRO cart
            return Object.assign({}, state, { reviewedCart: payload });
        case MROCartActionTypes.UPDATE_CART: {
            const prefetchEnabled = typeof window !== "undefined" && window.__headers__?.cartpageprefetch === "true";
            const allSubcartsSelected = validateAllSubcartsSelectedInMROCart(payload.reviewedCart, payload.localCart);
            const prefetchBackSyncEnabled = prefetchEnabled && allSubcartsSelected;
            const partialPrefetchBackSyncEnabled = prefetchEnabled && !allSubcartsSelected && !isQuickCheckoutFlow();
            const enabledBackSyncForPartialPrefetch = prefetchBackSyncEnabled || partialPrefetchBackSyncEnabled;

            let cartResponse: { last_updated_timestamp: string; response: Record<string, unknown> } | undefined;
            if (enabledBackSyncForPartialPrefetch) {
                // Use original prefetch timestamp for initial load (preserves TTL clock),
                // Date.now() for real API responses (background refresh, mutations)
                const prefetchTs = window.__prefetchTimestamp__;
                const timestamp = prefetchTs || String(Date.now());
                // Clear after first use — subsequent UPDATE_CART dispatches use Date.now()
                if (prefetchTs) {
                    window.__prefetchTimestamp__ = undefined;
                }

                let response = payload.reviewedCart as unknown as Record<string, unknown>;
                if (partialPrefetchBackSyncEnabled) {
                    response = {};
                }

                cartResponse = {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    last_updated_timestamp: timestamp,
                    response,
                };
            }
            // Update both local and reviewed MRO cart, and sync with bridge
            getBridgeSdk().updateLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
                    marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                    cartType: getMROCartType(),
                },
                data: {
                    ...(state.localCart || {}),
                    ...(payload?.localCart || {}),
                },
                ...(enabledBackSyncForPartialPrefetch && { cart_response: cartResponse }),
            });

            return {
                ...state,
                ...payload,
            };
        }
        case MROCartActionTypes.CLEAR_LOCAL_CART: {
            // Clear the local MRO cart.
            const incomingReviewedCart = (payload as { reviewedCart: MROReviewedCart }).reviewedCart;
            return {
                ...state,
                localCart: null,
                reviewedCart: {
                    ...reviewedCart,
                    ...(incomingReviewedCart || {}),
                },
            };
        }
        default:
            throw new Error(`unhandled MRO cart action type ${type}`);
    }
}
